import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';

// Replace 'CustomTable' with the name of your custom table component
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Stack from '@mui/system/Stack';



import { useTranslate } from 'src/locales';
import {
  useGetPlacementsQuery
} from 'src/services/applications/applications.service';

import _ from 'lodash';
import Scrollbar from 'src/components/scrollbar';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';
import PlacementsTableRow from 'src/sections/candidates/placements/placements-table-row';
import PlacementsTableToolbar from 'src/sections/candidates/placements/placements-table-toolbar';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { TenantType } from 'src/types/enums';
import { openPlacementEditor } from 'src/store/slices/jobs/jobsSlice';
import { useAppDispatch } from 'src/store/store';

interface ApplicationPlacementsProps {

}

const DEFAULT_PAGE = {
  page: 1,
  per_page: 10,
};

const PlacementsTable: React.FC<ApplicationPlacementsProps> = () => {

  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const dispatch = useAppDispatch();

  const tableLabels = useMemo(
    () => [
      {
        id: 'candidate_name',
        label: t('jobs.detail.placements.table_labels.candidate_name'),
      },
      {
        id: 'job_title',
        label: t('jobs.detail.placements.table_labels.job_title'),
      },
      ...(tenantType === TenantType.Recruiter ? [{
        id: 'client',
        label: t('jobs.detail.placements.table_labels.client'),
      }] : []),
      ...(tenantType === TenantType.Client ? [{
        id: 'recruiter',
        label: t('jobs.detail.placements.table_labels.recruiter'),
      }] : []),
      {
        id: 'stage',
        label: t('jobs.detail.placements.table_labels.stage'),
      },
      {
        id: 'placed_on',
        label: t('jobs.detail.placements.table_labels.placed_on'),
      },
      {
        id: ''
      },
    ],
    [t, tenantType]
  );

  const [filterQuery, setFilterQuery] = useState<string>('');
  const [pageOptions, setPageOptions] = useState(DEFAULT_PAGE);

  const table = useTable();

  const { currentData: placementData, isLoading } = useGetPlacementsQuery({
    pageOptions,
    searchQuery: filterQuery
  });

  const handlePageChange = useCallback(
    (_event: any, pageNumber: number) => {
      setPageOptions((prev) => ({ ...prev, page: pageNumber++ }));
    },
    [setPageOptions]
  );

  const handleRowsPerPageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageOptions((prev) => ({ ...prev, per_page: +event.target.value }));
    },
    [setPageOptions]
  );

  const handleFilters = useMemo(
    () => _.debounce((filter, value: string) => {
      if (filter === 'search_query') {
        setFilterQuery(value as string);
      }

      setPageOptions((prev) => ({
        ...prev,
        page: 1, // Reset to first page on filter change
      }));
    }, 500),
    [setFilterQuery]
  );

  const clearFilters = useCallback(() => {
    setFilterQuery('');
  }, []);

  const handleEditPlacement = useCallback((placementId: string) => {
    dispatch(openPlacementEditor({ placementId }));
  }, [dispatch]);

  const notFound = !placementData || !placementData.count;

  return (
    <Card>

      {
        isLoading ? (
          <TableSkeleton />
        )
          : (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <PlacementsTableToolbar
                  table={table}
                  onFilters={handleFilters}
                  clearFilters={clearFilters}
                />
              </Stack>

              <TableContainer>
                <Scrollbar>
                  <Table sx={{ minWidth: 680 }} size={table?.dense ? 'small' : 'medium'}>
                    <TableHeadCustom headLabel={tableLabels} />
                    <TableBody>
                      {placementData?.results.map((row) => (
                        <PlacementsTableRow key={row.id} row={row} onEditPlacement={handleEditPlacement} />
                      )) ?? []}

                      <TableNoData notFound={notFound} />
                    </TableBody>
                  </Table>
                </Scrollbar>
                <TablePaginationCustom
                  count={placementData?.count ?? 0}
                  page={pageOptions.page}
                  rowsPerPage={pageOptions.per_page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  dense={table?.dense}
                  onChangeDense={table?.onChangeDense}
                />
              </TableContainer>
            </>
          )
      }
    </Card>
  );
};


export default PlacementsTable;
